import BaseModal from 'components/CommonComponents/BaseModal'
import React, { useCallback, useState } from 'react'
import { IPop, RootState } from 'constants/interfaces'
import { Box, Button, Flex, IconButton, Image, Text, useDisclosure, useToast } from '@chakra-ui/core'
import colors from 'constants/colors'
import PopAvatar from './PopAvatar'
import { PopType } from './utils'
import RePopBody from './RePopBody'
import PopSubmitBtn from './PopSubmitBtn'
import { useMediaState } from 'hooks/useMediaState'
import AudiencePublicationsModal from '../CommonComponents/AudiencePublicationModal'
import GroupsTree from '../CommonComponents/GroupsTree'
import { UsersList } from '../CommonComponents/UsersListModal'
import strings from 'constants/strings'
import { useAudienceState } from 'hooks/useAudienceState'
import { FaUsers } from 'react-icons/fa'
import { TooltipList } from '../CommonComponents/TooltipList'
import { MdEdit } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'
import { editPop } from 'redux/actions/pops'
import { AppDispatch } from 'config/redux'
import useLocaleText from 'components/useLocaleText'
import { TagsList } from '../CommonComponents/TagsListModal'
import { OptionType } from 'components/NewsFeed/constants'

interface IProps {
  isOpen: boolean
  onClose: () => void
  firstPopInStack: IPop
  handleSubmit: (popType: number | undefined) => void
}
export const RePop = ({ isOpen, onClose, firstPopInStack, handleSubmit }: IProps) => {
  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()
  const pop_recipients = useLocaleText('pop_recipients')
  const { isOpen: isAudienceMOdalOpen, onOpen: onOpenAudienceModal, onClose: onCloseAudienceModal } = useDisclosure()

  const [isEditMode, setIsEditMode] = useState(false)
  const [popTitle, setPopTitle] = useState<string>(firstPopInStack?.pop_title)
  const [popBody, setPopBody] = useState<string>(firstPopInStack?.pop_body)

  const {
    config: { groups, user_tags, tags },
    activeGroupID,
  } = useSelector((state: RootState) => state.config)
  const { isLoading } = useSelector((state: RootState) => state.pops)

  const {
    uploadedImagesURL,
    handleSingleMediaUploading,
    isUploadLoading,
    addMediaHandler,
    imageInputRef,
    setUploadedImagesURL,
  } = useMediaState({
    images: [firstPopInStack?.pop_image_url],
  })

  const {
    selectedUsersUids,
    setSelectedUsersUids,
    selectedGroupsIds,
    selectedTagsIds,
    setSelectedGroupsIds,
    setSelectedTagsIds,
    groupsToPayload,
    setGroupsToPayload,
    namesForTooltip,
    filteredRerailUsersByTreeUsers,
    treeUsers,
    treeGroups,
  } = useAudienceState({
    users: firstPopInStack?.repop_users,
    groups: firstPopInStack?.repop_groups,
    tagIds: firstPopInStack?.repop_tags,
    selectedOptionType: OptionType.POP,
  })

  const handleRevertChanges = useCallback(() => {
    setPopTitle(firstPopInStack?.pop_title)
    setPopBody(firstPopInStack?.pop_body)
    setUploadedImagesURL([firstPopInStack?.pop_image_url])
    setSelectedUsersUids(firstPopInStack?.repop_users ?? [])
    setSelectedTagsIds(firstPopInStack?.repop_tags ?? [])
    setSelectedGroupsIds(firstPopInStack?.repop_groups ?? [])
  }, [firstPopInStack, setSelectedGroupsIds, setSelectedTagsIds, setSelectedUsersUids, setUploadedImagesURL])

  const handleSaveChanges = async () => {
    if ((selectedUsersUids.length === 0 && selectedTagsIds.length === 0) || !popTitle || !popBody) {
      toast({
        title: 'Error',
        description: 'Please fill all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const body = {
      pop_id: firstPopInStack?.pk,
      data: {
        pop_title: popTitle,
        pop_body: popBody,
        pop_image_url: uploadedImagesURL.length ? uploadedImagesURL[0] : '',
        repop_users: selectedUsersUids,
        repop_groups: selectedGroupsIds,
        repop_tags: selectedTagsIds,
      },
    }

    const res = await dispatch(editPop({ body }))
    if (res) {
      setIsEditMode((prev) => !prev)
    }
  }

  const renderViewOptionModal = useCallback(
    (viewOption: number, setViewOption: React.Dispatch<React.SetStateAction<number>> | any) => {
      switch (viewOption) {
        case 0:
          return (
            <GroupsTree
              isOpen={isOpen}
              selectedGroupID={selectedGroupsIds}
              mode={strings.PARTIAL_MULTILPE_MODE}
              setViewOption={setViewOption}
              setSelectedGroupID={setSelectedGroupsIds}
              setGroupsToPayload={setGroupsToPayload}
              groupsToPayload={groupsToPayload}
              isAllowSubmitWhithoutGroups={true}
              treeGroups={treeGroups}
            />
          )
        case 1:
          return (
            <UsersList
              isOpen={isOpen}
              onClose={onClose}
              setViewOption={setViewOption}
              selectedUsersUids={selectedUsersUids}
              setSelectedUsersUids={setSelectedUsersUids}
              treeUsers={filteredRerailUsersByTreeUsers}
            />
          )
        case 2:
          const tagsListToRender = () => {
            const usersTags = user_tags ?? []
            const currentGroupTags = groups?.[activeGroupID!]?.tags ?? []
            const allTags = Array.from(new Set([...usersTags, ...currentGroupTags]))
            return tags.filter((tag) => allTags.includes(tag.sk))
          }
          return (
            <TagsList
              isOpen={true}
              onClose={onClose}
              setViewOption={setViewOption}
              tags={tagsListToRender()}
              selectedTagsIds={selectedTagsIds}
              setSelectedTagsIds={setSelectedTagsIds}
            />
          )
        default:
          break
      }
    },
    [
      activeGroupID,
      filteredRerailUsersByTreeUsers,
      groups,
      groupsToPayload,
      isOpen,
      onClose,
      selectedGroupsIds,
      selectedTagsIds,
      selectedUsersUids,
      setGroupsToPayload,
      setSelectedGroupsIds,
      setSelectedTagsIds,
      setSelectedUsersUids,
      tags,
      treeGroups,
      user_tags,
    ]
  )

  return (
    <>
      <BaseModal
        display={isAudienceMOdalOpen ? 'none' : 'block'}
        isOpen={isOpen}
        onClose={onClose}
        closeOnEsc={false}
        overflowY="unset"
        maxWidth="32rem"
        hideCloseButton={true}
        isShowAnimation={true}
        headerContent={
          <Flex minH="100px" justifyContent="space-between">
            <PopAvatar {...firstPopInStack} />
            {firstPopInStack?.pop_icon_url && (
              <Box
                width="50px"
                height="50px"
                pos="absolute"
                borderRadius="120px"
                left="50%"
                transform="translateX(-50%)"
                top="45px"
                background={colors.greyLight3}
              >
                <Image src={firstPopInStack.pop_icon_url} width="40px" height="30px" m="9px auto" />
              </Box>
            )}

            {isEditMode ? (
              <>
                <Flex flexDir="column" justifyContent="space-between">
                  <IconButton
                    isDisabled={isLoading}
                    onClick={handleRevertChanges}
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    aria-label="Search database"
                    icon="repeat"
                  />
                  <TooltipList items={namesForTooltip}>
                    <Button
                      fontSize="16px"
                      isDisabled={isLoading}
                      h="auto"
                      fontWeight="500"
                      p="5px"
                      onClick={onOpenAudienceModal}
                    >
                      <FaUsers />
                      <Text mx="2">{pop_recipients}</Text>
                    </Button>
                  </TooltipList>
                </Flex>

                <Button isDisabled={isLoading} onClick={handleSaveChanges}>
                  save
                </Button>
              </>
            ) : (
              firstPopInStack.pop_type !== PopType.FAKE_POP && (
                <Button onClick={() => setIsEditMode((prev) => !prev)}>
                  <MdEdit />
                  <Text mx="2">Edit</Text>
                </Button>
              )
            )}
          </Flex>
        }
        bodyContent={
          <RePopBody
            isEditMode={isEditMode}
            popBody={popBody}
            popTitle={popTitle}
            popImageURL={uploadedImagesURL?.[0]}
            audio={firstPopInStack?.audio ?? ''}
            video={firstPopInStack?.video ?? ''}
            created_by={firstPopInStack.created_by}
            setPopBody={setPopBody}
            setPopTitle={setPopTitle}
            handleSingleMediaUploading={handleSingleMediaUploading}
            isUploadLoading={isUploadLoading}
            addMedia={addMediaHandler}
            imageInputRef={imageInputRef}
            isLoading={isLoading}
            setUploadedImagesURL={setUploadedImagesURL}
          />
        }
        footerContent={
          <Flex w="100%" justifyContent="center" alignItems="center" flexDir="column">
            <>
              <PopSubmitBtn
                isDisabled={isEditMode || isLoading}
                hasIcon={true}
                onClose={handleSubmit}
                btnText={firstPopInStack.repop_main_btn ?? ''}
                pop_initiator={firstPopInStack.pop_initiator}
                popType={firstPopInStack.pop_type}
              />
              <Button
                isDisabled={isEditMode || isLoading}
                onClick={() => handleSubmit(PopType.DEFAULT_POP)}
                mb="10pt"
                background="transparent"
                color="#879AF2"
                _focus={{ boxShadow: 'none' }}
                _hover={{ background: 'transparent' }}
                _active={{ background: 'transparent' }}
              >
                {firstPopInStack.repop_sec_btn}
              </Button>
            </>
          </Flex>
        }
      />
      <AudiencePublicationsModal
        isOpen={isAudienceMOdalOpen}
        groups={treeGroups}
        users={treeUsers}
        onClose={onCloseAudienceModal}
        title={pop_recipients}
        renderViewOptionModal={renderViewOptionModal}
        selectedUsersUids={selectedUsersUids}
        groupsToPayload={selectedGroupsIds ?? []}
        selectedTagsIds={selectedTagsIds ?? []}
        selectedOptionType="30"
      />
    </>
  )
}
